/** Estado de una rifa (alineado con app mobile) */
export type RaffleStatus = "upcoming" | "active" | "drawing" | "completed" | "cancelled";

/** Modo de finalización */
export type RaffleEndMode = "by_date" | "by_sales" | "by_sales_or_date";

/** Tipo de premio */
export type RafflePrizeType = "vehicle" | "custom_item";

export interface CustomRaffleItem {
  name: string;
  image: string;
  video?: string;
  characteristics: string[];
  value?: number;
}

export interface RaffleEntry {
  id: string;
  raffleId: string;
  userId: string;
  userName: string;
  userEmail?: string;
  selectedNumber: number;
  cedula?: string;
  isPaid?: boolean;
  paymentAmount?: number;
  entryDate: Date | { seconds: number } | { toDate: () => Date };
  isWinner?: boolean;
}

export interface RaffleWinner {
  userId: string;
  userName: string;
  selectedNumber: number;
  cedula?: string;
  announcedAt: Date | { seconds: number } | { toDate: () => Date };
}

export interface VehicleRaffle {
  id: string;
  title: string;
  description: string;
  images: string[];
  prizeType: RafflePrizeType;
  prizeValue: number;
  vehicleId?: string;
  vehicleName?: string;
  vehicleBrand?: string;
  vehicleYear?: number;
  vehicleImage?: string;
  vehicleValue?: number;
  customItem?: CustomRaffleItem;
  totalNumbers: number;
  availableNumbers?: number[];
  selectedNumbers?: Record<number, string>;
  isFree: boolean;
  pricePerNumber?: number;
  maxNumbersPerUser?: number;
  endMode: RaffleEndMode;
  targetSales?: number;
  status: RaffleStatus;
  startDate: Date | { seconds: number } | { toDate: () => Date };
  endDate?: Date | { seconds: number } | { toDate: () => Date } | null;
  drawDate?: Date | { seconds: number } | { toDate: () => Date } | null;
  totalParticipants: number;
  winner?: RaffleWinner;
  winningNumber?: number;
  rules: string[];
  termsAndConditions: string;
  isActive: boolean;
  isFeatured?: boolean;
  isPinned?: boolean;
  createdAt: Date | { seconds: number } | { toDate: () => Date };
  updatedAt?: Date | { seconds: number } | { toDate: () => Date };
}

function toDate(v: Date | { seconds: number } | { toDate: () => Date } | undefined | null): Date | undefined {
  if (v == null) return undefined;
  if (v instanceof Date) return v;
  if (typeof (v as { toDate?: () => Date }).toDate === "function") return (v as { toDate: () => Date }).toDate();
  if (typeof (v as { seconds?: number }).seconds === "number") return new Date((v as { seconds: number }).seconds * 1000);
  return undefined;
}

export function normalizeRaffle(doc: { id: string } & Record<string, unknown>): VehicleRaffle {
  const d = doc as Record<string, unknown>;
  return {
    id: doc.id,
    title: (d.title as string) ?? "",
    description: (d.description as string) ?? "",
    images: (d.images as string[]) ?? [],
    prizeType: (d.prizeType as RafflePrizeType) ?? "vehicle",
    prizeValue: Number(d.prizeValue) ?? 0,
    vehicleId: d.vehicleId as string | undefined,
    vehicleName: d.vehicleName as string | undefined,
    vehicleBrand: d.vehicleBrand as string | undefined,
    vehicleYear: d.vehicleYear as number | undefined,
    vehicleImage: d.vehicleImage as string | undefined,
    vehicleValue: d.vehicleValue as number | undefined,
    customItem: d.customItem as CustomRaffleItem | undefined,
    totalNumbers: Number(d.totalNumbers) ?? 0,
    availableNumbers: (d.availableNumbers as number[]) ?? [],
    selectedNumbers: (d.selectedNumbers as Record<number, string>) ?? {},
    isFree: Boolean(d.isFree),
    pricePerNumber: d.pricePerNumber as number | undefined,
    maxNumbersPerUser: (d.maxNumbersPerUser as number) ?? 1,
    endMode: (d.endMode as RaffleEndMode) ?? "by_date",
    targetSales: d.targetSales as number | undefined,
    status: (d.status as RaffleStatus) ?? "upcoming",
    startDate: toDate(d.startDate as never) ?? new Date(),
    endDate: toDate(d.endDate as never) ?? undefined,
    drawDate: toDate(d.drawDate as never) ?? undefined,
    totalParticipants: Number(d.totalParticipants) ?? 0,
    winner: d.winner as RaffleWinner | undefined,
    winningNumber: d.winningNumber as number | undefined,
    rules: (d.rules as string[]) ?? [],
    termsAndConditions: (d.termsAndConditions as string) ?? "",
    isActive: Boolean(d.isActive),
    isFeatured: d.isFeatured as boolean | undefined,
    isPinned: d.isPinned as boolean | undefined,
    createdAt: toDate(d.createdAt as never) ?? new Date(),
    updatedAt: toDate(d.updatedAt as never),
  };
}

export function normalizeRaffleEntry(doc: { id: string } & Record<string, unknown>): RaffleEntry {
  const d = doc as Record<string, unknown>;
  return {
    id: doc.id,
    raffleId: (d.raffleId as string) ?? "",
    userId: (d.userId as string) ?? "",
    userName: (d.userName as string) ?? "",
    userEmail: d.userEmail as string | undefined,
    selectedNumber: Number(d.selectedNumber) ?? 0,
    cedula: d.cedula as string | undefined,
    isPaid: d.isPaid as boolean | undefined,
    paymentAmount: d.paymentAmount as number | undefined,
    entryDate: (d.entryDate as RaffleEntry["entryDate"]) ?? new Date(),
    isWinner: d.isWinner as boolean | undefined,
  };
}
